/**
 * Stripe-ready logo templates for Mind Body Spirit HYGIENE™ Tools.
 *   • Icon: 512×512 (square, for favicons / social avatars / Stripe "Icon")
 *   • Horizontal: 600×150 (for Stripe "Logo" / email headers)
 *   • Icon alt (dark): 512×512 dark variant
 */
import { useEffect } from "react";
import { Sparkles } from "lucide-react";

const Frame = ({ id, w, h, bg = "bg-white", children }) => (
  <div
    id={id}
    className={`relative overflow-hidden ${bg}`}
    style={{ width: `${w}px`, height: `${h}px` }}
  >
    {children}
  </div>
);

export default function StripeLogos() {
  useEffect(() => {
    document.title = "Brand Logos (auto-generated)";
  }, []);

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      <div className="max-w-[900px] mx-auto space-y-10">
        <h1 className="font-serif text-3xl text-gray-800">
          Brand logos (auto-generated)
        </h1>

        {/* ═══════════ 1. SQUARE ICON 512×512 ═══════════ */}
        <div>
          <p className="text-xs font-bold text-gray-600 mb-2">
            logo-icon-teal — 512×512 (for Stripe Icon)
          </p>
          <Frame id="logo-icon-teal" w={512} h={512}>
            <div className="absolute inset-0 bg-gradient-to-br from-[#068fa2] via-[#25a1c2] to-[#5EEAD4]" />
            <div className="absolute top-8 right-10 opacity-25">
              <Sparkles className="w-24 h-24 text-yellow-200" />
            </div>
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-10">
              <div className="inline-flex items-center gap-2 bg-yellow-300 text-[#068fa2] rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] mb-4">
                <Sparkles className="w-3 h-3" />
                I GET TO
              </div>
              <h2
                className="font-serif text-white leading-[0.9] mb-2"
                style={{ fontSize: "88px" }}
              >
                MBS
              </h2>
              <p className="text-white/85 text-xs font-bold tracking-[0.35em] uppercase">
                Hygiene Tools
              </p>
              <div className="mt-5 w-16 h-[2px] bg-yellow-300/80 rounded-full" />
              <p className="text-white/60 text-[9px] tracking-[0.25em] uppercase mt-4">
                Mind · Body · Spirit
              </p>
            </div>
          </Frame>
        </div>

        {/* ═══════════ 2. HORIZONTAL LOGO 600×150 ═══════════ */}
        <div>
          <p className="text-xs font-bold text-gray-600 mb-2">
            logo-horizontal — 600×150 (for Stripe Logo / email headers)
          </p>
          <Frame id="logo-horizontal" w={600} h={150} bg="bg-white">
            <div className="relative z-10 h-full flex items-center px-8 gap-5">
              {/* Icon tile */}
              <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-[#068fa2] to-[#25a1c2] flex flex-col items-center justify-center shrink-0 shadow-md relative overflow-hidden">
                <div className="absolute top-1 right-1 opacity-20">
                  <Sparkles className="w-8 h-8 text-yellow-200" />
                </div>
                <h3 className="font-serif text-white leading-none" style={{ fontSize: "40px" }}>
                  MBS
                </h3>
                <p className="text-yellow-200 text-[9px] font-bold tracking-[0.2em] uppercase mt-1">
                  I GET TO
                </p>
              </div>

              {/* Wordmark */}
              <div>
                <h2
                  className="font-serif text-[#042F2E] leading-[0.95] tracking-tight"
                  style={{ fontSize: "36px" }}
                >
                  Mind Body Spirit
                </h2>
                <p className="text-[#25a1c2] text-lg font-serif italic mt-0.5 leading-tight">
                  Hygiene Tools
                </p>
                <p className="text-[#64748B] text-[10px] font-bold tracking-[0.3em] uppercase mt-1">
                  by Temple Franklin
                </p>
              </div>
            </div>
          </Frame>
        </div>

        {/* ═══════════ 3. DARK ICON 512×512 (alt for light backgrounds) ═══════════ */}
        <div>
          <p className="text-xs font-bold text-gray-600 mb-2">
            logo-icon-light — 512×512 (alt, for dark-mode checkouts)
          </p>
          <Frame id="logo-icon-light" w={512} h={512} bg="bg-white">
            <div className="absolute top-8 right-10 opacity-10">
              <Sparkles className="w-24 h-24 text-[#25a1c2]" />
            </div>
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-10">
              <div className="inline-flex items-center gap-2 bg-[#068fa2] text-yellow-300 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] mb-4">
                <Sparkles className="w-3 h-3" />
                I GET TO
              </div>
              <h2
                className="font-serif leading-[0.9] mb-2 bg-gradient-to-br from-[#068fa2] to-[#25a1c2] bg-clip-text text-transparent"
                style={{ fontSize: "88px" }}
              >
                MBS
              </h2>
              <p className="text-[#25a1c2] text-xs font-bold tracking-[0.35em] uppercase">
                Hygiene Tools
              </p>
              <div className="mt-5 w-16 h-[2px] bg-[#25a1c2]/60 rounded-full" />
              <p className="text-[#64748B] text-[9px] tracking-[0.25em] uppercase mt-4">
                Mind · Body · Spirit
              </p>
            </div>
          </Frame>
        </div>
      </div>
    </div>
  );
}
